import { StyleSheet, TextStyle, ViewStyle } from 'react-native';

// 主题色
const COLORS = {
  light: {
    primary: '#007AFF',
    success: '#34C759',
    warning: '#FF9500',
    error: '#FF3B30',
    border: '#E5E5EA',
    borderFocus: '#007AFF',
    background: '#F9F9F9',
    backgroundDisabled: '#F2F2F7',
    text: '#1C1C1E',
    textSecondary: '#8E8E93',
    textDisabled: '#C7C7CC',
    placeholder: '#C7C7CC',
    label: '#3C3C43',
    hint: '#8E8E93',
    icon: '#8E8E93',
    clear: '#C7C7CC',
  },
  dark: {
    primary: '#0A84FF',
    success: '#30D158',
    warning: '#FF9F0A',
    error: '#FF453A',
    border: '#38383A',
    borderFocus: '#0A84FF',
    background: '#1C1C1E',
    backgroundDisabled: '#2C2C2E',
    text: '#FFFFFF',
    textSecondary: '#8E8E93',
    textDisabled: '#48484A',
    placeholder: '#636366',
    label: '#EBEBF5',
    hint: '#8E8E93',
    icon: '#8E8E93',
    clear: '#636366',
  },
};

// 尺寸配置
const SIZES = {
  small: {
    height: 36,
    fontSize: 14,
    iconSize: 16,
    paddingHorizontal: 10,
    borderRadius: 6,
    labelFontSize: 12,
  },
  medium: {
    height: 44,
    fontSize: 16,
    iconSize: 20,
    paddingHorizontal: 12,
    borderRadius: 8,
    labelFontSize: 14,
  },
  large: {
    height: 52,
    fontSize: 18,
    iconSize: 24,
    paddingHorizontal: 16,
    borderRadius: 10,
    labelFontSize: 16,
  },
};

export type ThemeType = 'light' | 'dark';
export type SizeType = 'small' | 'medium' | 'large';

export const getColors = (theme: ThemeType) => COLORS[theme];
export const getSizes = (size: SizeType) => SIZES[size];

export const createStyles = (theme: ThemeType = 'light', size: SizeType = 'medium') => {
  const colors = COLORS[theme];
  const sizeConfig = SIZES[size];

  return StyleSheet.create({
    // 容器
    container: {
      marginBottom: 16,
    },
    
    // 标签
    labelContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 6,
    },
    labelContainerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    label: {
      fontSize: sizeConfig.labelFontSize,
      fontWeight: '500',
      color: colors.label,
    },
    labelLeft: {
      marginRight: 12,
      minWidth: 80,
    },
    required: {
      color: colors.error,
      marginLeft: 2,
    },
    
    // 输入框容器
    inputWrapper: {
      flex: 1,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: sizeConfig.height,
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: sizeConfig.borderRadius,
      paddingHorizontal: sizeConfig.paddingHorizontal,
    },
    inputContainerFocused: {
      borderColor: colors.border,
      borderWidth: 1,
    },
    inputContainerError: {
      borderColor: colors.error,
    },
    inputContainerSuccess: {
      borderColor: colors.success,
    },
    inputContainerWarning: {
      borderColor: colors.warning,
    },
    inputContainerDisabled: {
      backgroundColor: colors.backgroundDisabled,
    },
    inputContainerReadonly: {
      backgroundColor: colors.backgroundDisabled,
    },
    
    // 形状变体
    inputContainerRound: {
      borderRadius: sizeConfig.height / 2,
    },
    inputContainerUnderline: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderBottomWidth: 1,
      borderRadius: 0,
      paddingHorizontal: 0,
    },
    inputContainerUnderlineFocused: {
      borderBottomWidth: 2,
      borderBottomColor: colors.borderFocus,
    },
    
    // 输入框
    input: {
      flex: 1,
      fontSize: sizeConfig.fontSize,
      color: colors.text,
      paddingVertical: 8,
      paddingHorizontal: 4,
    } as TextStyle,
    inputDisabled: {
      color: colors.textDisabled,
    },
    inputReadonly: {
      color: colors.textSecondary,
    },
    
    // textarea
    textarea: {
      textAlignVertical: 'top',
      paddingTop: 10,
    } as TextStyle,
    
    // 图标
    leftIcon: {
      marginRight: 8,
    },
    rightIcon: {
      marginLeft: 8,
    },
    iconTouchable: {
      padding: 4,
    },
    
    // 清除按钮
    clearButton: {
      padding: 4,
      marginLeft: 4,
    },
    
    // 密码切换
    passwordToggle: {
      padding: 4,
      marginLeft: 4,
    },
    
    // 步进器
    stepperContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: 8,
    },
    stepperButton: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    stepperButtonDisabled: {
      opacity: 0.5,
    },
    stepperDivider: {
      width: 8,
    },
    
    // 底部区域
    footer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 4,
      paddingHorizontal: 2,
    },
    footerLeft: {
      flex: 1,
    },
    footerRight: {},
    
    // 提示文字
    hint: {
      fontSize: 12,
      color: colors.hint,
    },
    
    // 错误文字
    error: {
      fontSize: 12,
      color: colors.error,
    },
    
    // 字数统计
    count: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    countWarning: {
      color: colors.warning,
    },
    countError: {
      color: colors.error,
    },
    
    // 浮动标签
    floatingLabelContainer: {
      position: 'absolute',
      left: sizeConfig.paddingHorizontal,
      top: sizeConfig.height / 2 - sizeConfig.labelFontSize / 2,
      backgroundColor: 'transparent',
    },
    floatingLabelActive: {
      top: -sizeConfig.labelFontSize / 2 - 2,
      backgroundColor: colors.background,
      paddingHorizontal: 4,
    },
    floatingLabel: {
      fontSize: sizeConfig.fontSize,
      color: colors.placeholder,
    },
    floatingLabelActive2: {
      fontSize: sizeConfig.labelFontSize - 2,
      color: colors.primary,
    },
    
    // 左侧标签布局
    leftLabelContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    
    // 电话区号
    phonePrefix: {
      fontSize: sizeConfig.fontSize,
      color: colors.text,
      marginRight: 8,
      paddingVertical: 8,
    },
    phoneDivider: {
      width: 1,
      height: 20,
      backgroundColor: colors.border,
      marginRight: 8,
    },
  });
};

export default createStyles;
